// Lightweight, fail-safe ad/analytics event helpers.
//
// We fire conversions to two sinks when present:
//   - Microsoft Advertising UET   (window.uetq)   — the ad credits we're spending
//   - Google Analytics gtag       (window.gtag)   — already loaded in index.html
//
// Every call no-ops silently if the tag isn't on the page yet (e.g. the UET
// tag ID hasn't been pasted into index.html, or an ad-blocker stripped it),
// so callers never need to guard. Keep these calls cheap and side-effect-free
// beyond the push.

/**
 * Fire a Microsoft UET custom event / goal.
 * Microsoft matches conversion goals by `event_action` (and optionally
 * category/label), configured in the Microsoft Ads UI.
 *
 * @param {string} action  e.g. 'blueprint_lead', 'blueprint_ready'
 * @param {object} [opts]  { category, label, value }
 */
export function uetEvent(action, opts = {}) {
  if (typeof window === 'undefined') return;
  try {
    window.uetq = window.uetq || [];
    window.uetq.push('event', action, {
      event_category: opts.category || 'blueprint',
      ...(opts.label != null ? { event_label: opts.label } : {}),
      ...(opts.value != null ? { event_value: opts.value, revenue_value: opts.value } : {}),
    });
  } catch {
    /* never let analytics break the app */
  }
}

/** Fire a GA4 event via gtag (loaded in index.html). No-ops if absent. */
export function gaEvent(action, params = {}) {
  if (typeof window === 'undefined') return;
  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', action, params);
    }
  } catch {
    /* ignore */
  }
}

/**
 * Convenience: fire the same conversion to both UET and GA at once.
 * Use for the events that matter to ad optimization.
 */
export function trackConversion(action, { category = 'blueprint', label, value } = {}) {
  uetEvent(action, { category, label, value });
  gaEvent(action, {
    ...(label != null ? { label } : {}),
    ...(value != null ? { value } : {}),
  });
}
